package com.essntl.features.task.data.repository

import com.essntl.core.supabase.*
import com.essntl.core.utils.repository.Repository
import com.essntl.features.task.data.supabase.TaskDto
import com.essntl.features.task.data.supabase.TaskSupabaseDataSource
import com.essntl.features.task.data.supabase.toRequestMap
import com.essntl.features.task.domain.model.TaskFilterOptions
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.datetime.*
import org.koin.core.annotation.Single

@Single(binds = [TaskRepository::class])
internal class TaskRepositoryImpl internal constructor(
    private val supabase: TaskSupabaseDataSource,
) : Repository(), TaskRepository {
    override suspend fun getById(id: String): Result<TaskModel> =
        runCatching {
            supabase
                .getById(id = id, columns = TaskDto.getColumns())
                .toModel()
        }

    override suspend fun getAllByItineraryServiceIds(itineraryServiceIds: List<String>): Result<List<TaskModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = TaskDto.getColumns(),
                ) {
                    if (itineraryServiceIds.isNotEmpty()) {
                        filter {
                            or {
                                itineraryServiceIds.forEach { itineraryServiceId ->
                                    eq("itinerary_service_id", itineraryServiceId)
                                }
                            }
                        }
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getAllByItineraryId(itineraryId: String): Result<List<TaskModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = TaskDto.getColumns(),
                ) {
                    filter {
                        eq("itinerary_id", itineraryId)
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getAllByProposalId(proposalId: String): Result<List<TaskModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = TaskDto.getColumns(),
                ) {
                    filter {
                        eq("proposal_id", proposalId)
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getOverdueTasks(): Result<List<TaskModel>> =
        runCatching {
            val today = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
            supabase
                .getAll(
                    columns = TaskDto.getColumns(),
                ) {
                    filter {
                        // past to_do tasks
                        lt("due_date", today.toString())
                        eq("status", "TO_DO")
                    }
                    order("due_date", Order.DESCENDING)

                }.map { it.toModel() }
        }

    override suspend fun getAll(
        page: Long,
        limit: Int,
        filterOptions: TaskFilterOptions,
    ): Result<List<TaskModel>> =
        runCatching {
            supabase
                .getAll(
                    page = page,
                    limit = limit,
                    columns = TaskDto.getColumns(),
                ) {
                    filter {
                        if (filterOptions.weeklyTasks &&
                            filterOptions.weekDatesRange.isNotEmpty()
                        ) {
                            // weekly tasks
                            isIn(
                                "due_date",
                                filterOptions.weekDatesRange,
                            )
                        } else if (filterOptions.dueDate.toString().isNotEmpty()) {
                            // tasks per due_date
                            eq("due_date", filterOptions.dueDate)
                        }

                        if (filterOptions.selectedProfileIds.isNotEmpty()) {
                            // Owner Tasks
                            and {
                                filterOptions.selectedProfileIds.forEach { profileId ->
                                    eq("owner_id", profileId)
                                }
                            }
                        }

                        // Tasks per status
                        if (filterOptions.selectedTaskStatus.isNotEmpty()) {
                            and {
                                filterOptions.selectedTaskStatus.forEach { status ->
                                    eq("status", status)
                                }
                            }
                        }
                    }

                    order("due_date", Order.ASCENDING)
                }
                .map { it.toModel() }
        }

    override suspend fun getAllTodayTodoTasks(dueDate: String): Result<List<TaskModel>> =
        runCatching {
            supabase
                .getAll(columns = TaskDto.getColumns()) {
                    if (dueDate.isNotEmpty()) {
                        filter {
                            eq("due_date", dueDate)
                            eq("status", "TO_DO")
                        }
                    }
                    order("created_at", Order.DESCENDING)
                }
                .map { it.toModel() }
        }

    override suspend fun insert(task: TaskModel): Result<TaskModel> =
        runCatching {
            val requestMap = task.toRequestMap()
            supabase
                .insert(
                    requestMap = requestMap,
                    columns = TaskDto.getColumns(),
                ).toModel()
        }

    override suspend fun update(task: TaskModel): Result<TaskModel> =
        runCatching {
            val requestMap = task.toRequestMap()
            supabase.update(
                id = task.id,
                requestMap = requestMap,
                columns = TaskDto.getColumns(),
            ).toModel()
        }

    override suspend fun delete(id: String): Result<Unit> =
        runCatching {
            supabase.delete(id = id)
        }
}
